// /checklist — hub for all visa document checklists
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema, howToSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { VISA_TYPES, CATEGORIES } from "@/data/visa-types";
import { SCENARIOS } from "@/data/visa-checklists";
import { consolidateGraph } from "@/lib/schema-graph";
import { TrustLayer } from "@/components/TrustLayer";
import { FileCheck2, ArrowRight, ShieldCheck } from "lucide-react";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/checklist/")({
  head: () => {
    const url = `${SITE}/checklist`;
    const title = `Visa Document Checklist · ${VISA_TYPES.length} ประเภทวีซ่า × ${SCENARIOS.length} สถานการณ์ | NYC`;
    const desc = `Checklist เอกสารวีซ่าครบทุกประเภท: Non-B · LTR · DTV · Smart Visa · Retirement · Marriage · Elite — ครอบคลุมยื่นใหม่ ต่ออายุ เปลี่ยนนายจ้าง Work Permit และอีกมากมาย — ปรึกษาก่อนยื่นจริง ลดโอกาสเอกสารตีกลับ`;
    return {
      meta: [
        { title: ctaTitle(title, "/checklist", "th") }, { name: "description", content: ctaDesc(desc, "/checklist", "th")},
        { property: "og:title", content: ctaTitle(title, "/checklist", "th")}, { property: "og:description", content: ctaDesc(desc, "/checklist", "th")},
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/checklist")],
      scripts: [speakableSchema(),
        howToSchema({
          name: "วิธีใช้ Visa Document Checklist เตรียมเอกสารวีซ่า",
          description: "ขั้นตอนใช้ checklist เพื่อเตรียมเอกสารยื่นวีซ่าให้ครบ ไม่ต้องวิ่งซ้ำ — รวมแปล + Apostille + ยื่นจริง",
          totalTime: "PT7D", estimatedCost: { currency: "THB", value: "500" },
          steps: [
            { name: "เลือกประเภทวีซ่า", text: "คลิกประเภทวีซ่าที่จะยื่น (Non-B, LTR, DTV, Marriage, Retirement, ED, Smart, Elite, Schengen, US, UK, AU)" },
            { name: "ดาวน์โหลด Checklist + ติ๊กเอกสารที่มี", text: "เปรียบเทียบกับเอกสารต้นฉบับที่บ้าน — สิ่งที่ขาดให้เริ่มขอจากเขต/อำเภอ" },
            { name: "ส่งให้ NYC แปล + รับรอง", text: "ส่งภาพ checklist + เอกสารทาง LINE — ทีมจะ quote ภายใน 30 นาที", url: `${SITE}/translate` },
            { name: "รับ Apostille / Legalization (ถ้าวีซ่าต้อง)", text: "วีซ่าต่างประเทศส่วนใหญ่ต้องผ่าน MFA + Apostille — เราจัดการให้ครบ", url: `${SITE}/apostille` },
          ],
        }),
        consolidateGraph([
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc,
              hasPart: VISA_TYPES.map((v) => ({
                "@type": "WebPage", name: `${v.nameTh} Checklist`, url: `${SITE}/checklist/${v.id}`,
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "Visa Checklist", item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: ChecklistHub,
});

function ChecklistHub() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <FileCheck2 className="h-3.5 w-3.5" /> Visa Document Checklist 2026
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Checklist เอกสารวีซ่า<br className="hidden sm:block" />
            <span className="text-gold">{VISA_TYPES.length} ประเภท × {SCENARIOS.length} สถานการณ์</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            รวมรายการเอกสารครบทุกขั้นตอน — ยื่นครั้งแรก, ต่ออายุ, เปลี่ยนนายจ้าง, ครอบครัวติดตาม, Work Permit, และเปลี่ยนประเภทในไทย พร้อมเตือนกฎกติกาที่พลาดบ่อย
          </p>
          <div className="quick-answer mt-6 max-w-3xl rounded-2xl border border-gold/40 bg-gold/10 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gold">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">ขอวีซ่าไทยต้องใช้เอกสารอะไรบ้าง?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">เอกสารขึ้นกับประเภทวีซ่า — Non-B ใช้ 11 รายการ, LTR 14 รายการ, DTV 9 รายการ ครอบคลุมหนังสือเดินทาง รูปถ่าย เอกสารงาน/การเงิน NYC รวบรวม Checklist ปี 2026 ครบ 12 ประเภทวีซ่าไทยและ Schengen/US/UK/AU พร้อม link ไปบริการแปลและ Apostille ในรายการเดียว</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {VISA_TYPES.map((v) => {
            const cat = CATEGORIES[v.category];
            return (
              <Link
                key={v.id}
                to="/checklist/$visatype"
                params={{ visatype: v.id }}
                className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${cat.color}`}>
                    {cat.emoji} {cat.label}
                  </span>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
                </div>
                <div className="mt-3 font-display text-lg font-extrabold text-navy-deep group-hover:text-amber-600">
                  {v.nameTh}
                </div>
                <div className="mt-1 text-[12px] text-slate-500">{v.code} · {v.durationInitial}</div>
                <div className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-700">
                  <ShieldCheck className="h-3 w-3" /> {SCENARIOS.length} Checklist สถานการณ์
                </div>
              </Link>
            );
          })}
        </div>
      <RelatedHubGrid locale="th" />
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
